import { forwardRef, useState } from 'react'
import tw from 'twin.macro'
import type { Ref, ForwardRefRenderFunction } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'

import Button from '../Button/Button'
import type { ButtonDropdownProps } from './types'
import { ChevronDown, ChevronUp } from '../Icons'
import {
  DropdownMenuContentStyled,
  DropdownMenuItemStyled,
  DropdownMenuTriggerStyled,
} from './ButtonDropdown.styled'

function BaseButtonDropdown(
  {
    size = 'medium',
    palette = 'action',
    variation = 'primary',
    fullWidth = false,
    centered = false,
    label,
    children,
    dropdownButtonProps = {},
    ...rest
  }: ButtonDropdownProps,
  ref: Ref<HTMLButtonElement>,
) {
  const [open, setOpen] = useState(false)

  return (
    <div tw="flex">
      <Button
        ref={ref}
        size={size}
        palette={palette}
        variation={variation}
        fullWidth={fullWidth}
        centered={centered}
        css={tw`rounded-r-none border-r-0`}
        {...rest}
      >
        {label}
      </Button>
      <DropdownMenu.Root onOpenChange={setOpen}>
        <DropdownMenu.Trigger asChild>
          <DropdownMenuTriggerStyled
            size={size}
            palette={palette}
            variation={variation}
            {...dropdownButtonProps}
          >
            {open ? (
              <ChevronUp
                aria-label="Close options"
                height={size === 'small' ? 20 : 24}
                width={size === 'small' ? 20 : 24}
              />
            ) : (
              <ChevronDown
                aria-label="Open options"
                height={size === 'small' ? 20 : 24}
                width={size === 'small' ? 20 : 24}
              />
            )}
          </DropdownMenuTriggerStyled>
        </DropdownMenu.Trigger>
        {children}
      </DropdownMenu.Root>
    </div>
  )
}

// Since we are using forwardRef we had to give a little massage to the types hehe
const ButtonDropdown = forwardRef(
  BaseButtonDropdown,
) as unknown as ForwardRefRenderFunction<
  HTMLButtonElement,
  ButtonDropdownProps
> & {
  Content: typeof DropdownMenuContentStyled
  Item: typeof DropdownMenuItemStyled
}

ButtonDropdown.Content = DropdownMenuContentStyled
ButtonDropdown.Item = DropdownMenuItemStyled

export default ButtonDropdown
